const DEFAULT_API_BASE_URL =
  process.env.NODE_ENV === "production"
    ? "https://up-and-away-api.sleimanmehsen18.workers.dev/api/v1"
    : "http://localhost:8787/api/v1";

const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL || DEFAULT_API_BASE_URL
).replace(/\/+$/, "");
export const API_ORIGIN = new URL(API_BASE_URL).origin;

export class ApiError extends Error {
  constructor(message, { status, code, details, requestId } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
    this.requestId = requestId;
  }
}

const getQueryString = (values = {}) => {
  const query = new URLSearchParams();

  for (const [key, value] of Object.entries(values)) {
    if (Array.isArray(value)) {
      if (value.length > 0) query.set(key, value.join(","));
    } else if (value !== undefined && value !== null && value !== "") {
      query.set(key, String(value));
    }
  }

  const value = query.toString();
  return value ? `?${value}` : "";
};

const readApiResponse = async (response) => {
  if (response.status === 204) {
    return { success: true, data: null };
  }

  const isJson = response.headers
    .get("content-type")
    ?.includes("application/json");
  const payload = isJson ? await response.json() : null;

  if (!response.ok) {
    throw new ApiError(
      payload?.error?.message ||
        `Request failed with status ${response.status}`,
      {
        status: response.status,
        code: payload?.error?.code,
        details: payload?.error?.details,
        requestId: payload?.requestId || response.headers.get("x-request-id"),
      },
    );
  }

  return payload;
};

export const apiFetch = async (
  path,
  { method = "GET", body, accessToken, signal } = {},
) => {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    signal,
    headers: {
      Accept: "application/json",
      ...(body === undefined ? {} : { "Content-Type": "application/json" }),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  return readApiResponse(response);
};

const apiDownload = async (path, { accessToken, signal } = {}) => {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: "GET",
    signal,
    headers: {
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
  });

  if (!response.ok) {
    await readApiResponse(response);
  }

  return {
    blob: await response.blob(),
    filename:
      response.headers
        .get("content-disposition")
        ?.match(/filename="([^"]+)"/)?.[1] || "ups-label.gif",
  };
};

const saveBlob = ({ blob, filename }) => {
  const href = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(href);
};

const uploadAdminMedia = async (path, file, accessToken, { signal } = {}) => {
  const response = await fetch(`${API_BASE_URL}/admin/uploads/${path}`, {
    method: "POST",
    signal,
    headers: {
      Accept: "application/json",
      "Content-Type": file.type,
      Authorization: `Bearer ${accessToken}`,
    },
    body: file,
  });

  return readApiResponse(response);
};

const uploadAdminImage = (file, accessToken, options) =>
  uploadAdminMedia("images", file, accessToken, options);

const uploadAdminVideo = (file, accessToken, options) =>
  uploadAdminMedia("videos", file, accessToken, options);

export const api = {
  products: {
    list: (filters, options = {}) =>
      apiFetch(`/products${getQueryString(filters)}`, options),
    get: (slug, options = {}) =>
      apiFetch(`/products/${encodeURIComponent(slug)}`, options),
  },
  candles: {
    list: (filters, options = {}) =>
      apiFetch(`/candles${getQueryString(filters)}`, options),
  },
  contactInquiries: {
    create: (inquiry, options = {}) =>
      apiFetch("/contact-inquiries", {
        ...options,
        method: "POST",
        body: inquiry,
      }),
  },
  reservations: {
    create: (reservation, options = {}) =>
      apiFetch("/reservations", {
        ...options,
        method: "POST",
        body: reservation,
      }),
  },
  newsletterSubscriptions: {
    create: (subscription, options = {}) =>
      apiFetch("/newsletter-subscriptions", {
        ...options,
        method: "POST",
        body: subscription,
      }),
  },
  siteSettings: {
    get: (options = {}) => apiFetch("/site-settings", options),
  },
  legal: {
    terms: async (options = {}) => {
      const payload = await apiFetch("/legal/terms", options);
      return payload?.data ?? payload;
    },
  },
  testimonials: {
    list: (options = {}) => apiFetch("/testimonials", options),
    create: (testimonial, options = {}) =>
      apiFetch("/testimonials", {
        ...options,
        method: "POST",
        body: testimonial,
      }),
  },
  payments: {
    quote: (productId, quantity, options = {}) => {
      const { itemType, candleIds, ...requestOptions } = options;
      return apiFetch(
        `/payments/quote${getQueryString({
          itemType,
          productId,
          candleIds,
          quantity,
        })}`,
        requestOptions,
      );
    },
    createIntent: (payment, options = {}) =>
      apiFetch("/payments/create-payment-intent", {
        ...options,
        method: "POST",
        body: payment,
      }),
  },
  shipping: {
    quote: (shipping, options = {}) =>
      apiFetch("/shipping/quote", {
        ...options,
        method: "POST",
        body: shipping,
      }),
  },
  buildOrders: {
    quote: (items, options = {}) =>
      apiFetch("/build-orders/quote", {
        ...options,
        method: "POST",
        body: { items },
      }),
    createIntent: (payment, options = {}) =>
      apiFetch("/build-orders/create-payment-intent", {
        ...options,
        method: "POST",
        body: payment,
      }),
    finalize: (payment, options = {}) =>
      apiFetch("/build-orders/finalize", {
        ...options,
        method: "POST",
        body: payment,
      }),
  },
  orders: {
    create: (order, options = {}) =>
      apiFetch("/orders", {
        ...options,
        method: "POST",
        body: order,
      }),
  },
  auth: {
    login: (credentials, options = {}) =>
      apiFetch("/login", {
        ...options,
        method: "POST",
        body: credentials,
      }),
  },
  admin: {
    me: (accessToken, options = {}) =>
      apiFetch("/admin/me", { ...options, accessToken }),
    images: {
      upload: uploadAdminImage,
    },
    videos: {
      upload: uploadAdminVideo,
    },
    siteSettings: {
      get: (accessToken, options = {}) =>
        apiFetch("/admin/site-settings", { ...options, accessToken }),
      update: (settings, accessToken, options = {}) =>
        apiFetch("/admin/site-settings", {
          ...options,
          method: "PATCH",
          body: settings,
          accessToken,
        }),
    },
    legal: {
      terms: {
        get: async (accessToken, options = {}) => {
          const payload = await apiFetch("/admin/legal/terms", {
            ...options,
            accessToken,
          });
          return payload?.data ?? payload;
        },
        update: async (terms, accessToken, options = {}) => {
          const payload = await apiFetch("/admin/legal/terms", {
            ...options,
            method: "PUT",
            body: terms,
            accessToken,
          });
          return payload?.data ?? payload;
        },
      },
    },
    testimonials: {
      list: (accessToken, options = {}) =>
        apiFetch("/admin/testimonials", {
          ...options,
          accessToken,
        }),
      create: (testimonial, accessToken, options = {}) =>
        apiFetch("/admin/testimonials", {
          ...options,
          method: "POST",
          body: testimonial,
          accessToken,
        }),
      update: (id, testimonial, accessToken, options = {}) =>
        apiFetch(`/admin/testimonials/${encodeURIComponent(id)}`, {
          ...options,
          method: "PUT",
          body: testimonial,
          accessToken,
        }),
      remove: (id, accessToken, options = {}) =>
        apiFetch(`/admin/testimonials/${encodeURIComponent(id)}`, {
          ...options,
          method: "DELETE",
          accessToken,
        }),
    },
    products: {
      list: (filters, accessToken, options = {}) =>
        apiFetch(`/admin/products${getQueryString(filters)}`, {
          ...options,
          accessToken,
        }),
      create: (product, accessToken, options = {}) =>
        apiFetch("/admin/products", {
          ...options,
          method: "POST",
          body: product,
          accessToken,
        }),
      update: (slug, updates, accessToken, options = {}) =>
        apiFetch(`/admin/products/${encodeURIComponent(slug)}`, {
          ...options,
          method: "PATCH",
          body: updates,
          accessToken,
        }),
      remove: (slug, accessToken, options = {}) =>
        apiFetch(`/admin/products/${encodeURIComponent(slug)}`, {
          ...options,
          method: "DELETE",
          accessToken,
        }),
      unlinkCandle: (slug, candleId, accessToken, options = {}) =>
        apiFetch(
          `/admin/products/${encodeURIComponent(slug)}/candles/${encodeURIComponent(candleId)}`,
          {
            ...options,
            method: "DELETE",
            accessToken,
          },
        ),
    },
    candleProducts: {
      create: (candleProduct, accessToken, options = {}) =>
        apiFetch("/admin/candle-products", {
          ...options,
          method: "POST",
          body: candleProduct,
          accessToken,
        }),
      update: (id, updates, accessToken, options = {}) =>
        apiFetch(`/admin/candle-products/${encodeURIComponent(id)}`, {
          ...options,
          method: "PATCH",
          body: updates,
          accessToken,
        }),
      remove: (id, accessToken, options = {}) =>
        apiFetch(`/admin/candle-products/${encodeURIComponent(id)}`, {
          ...options,
          method: "DELETE",
          accessToken,
        }),
      linkProducts: (id, productIds, accessToken, options = {}) =>
        apiFetch(`/admin/candle-products/${encodeURIComponent(id)}/products`, {
          ...options,
          method: "POST",
          body: { productIds },
          accessToken,
        }),
    },
    contactInquiries: {
      list: (filters, accessToken, options = {}) =>
        apiFetch(`/admin/contact-inquiries${getQueryString(filters)}`, {
          ...options,
          accessToken,
        }),
      update: (id, updates, accessToken, options = {}) =>
        apiFetch(`/admin/contact-inquiries/${encodeURIComponent(id)}`, {
          ...options,
          method: "PATCH",
          body: updates,
          accessToken,
        }),
      remove: (id, accessToken, options = {}) =>
        apiFetch(`/admin/contact-inquiries/${encodeURIComponent(id)}`, {
          ...options,
          method: "DELETE",
          accessToken,
        }),
    },
    orders: {
      list: (filters, accessToken, options = {}) =>
        apiFetch(`/admin/orders${getQueryString(filters)}`, {
          ...options,
          accessToken,
        }),
      retryShipment: (id, accessToken, options = {}) =>
        apiFetch(
          `/admin/orders/${encodeURIComponent(id)}/shipments/retry`,
          {
            ...options,
            method: "POST",
            accessToken,
          },
        ),
      markDroppedOff: (id, accessToken, options = {}) =>
        apiFetch(
          `/admin/orders/${encodeURIComponent(id)}/shipments/dropped-off`,
          {
            ...options,
            method: "POST",
            accessToken,
          },
        ),
      voidShipment: (id, accessToken, options = {}) =>
        apiFetch(
          `/admin/orders/${encodeURIComponent(id)}/shipments/void`,
          {
            ...options,
            method: "POST",
            accessToken,
          },
        ),
      downloadLabel: async (id, labelIndex, accessToken, options = {}) =>
        saveBlob(
          await apiDownload(
            `/admin/orders/${encodeURIComponent(id)}/labels/${labelIndex}`,
            {
              ...options,
              accessToken,
            },
          ),
        ),
      remove: (id, accessToken, options = {}) =>
        apiFetch(`/orders/${encodeURIComponent(id)}`, {
          ...options,
          method: "DELETE",
          accessToken,
        }),
    },
    buildOrders: {
      list: (filters, accessToken, options = {}) =>
        apiFetch(`/admin/build-orders${getQueryString(filters)}`, {
          ...options,
          accessToken,
        }),
      retryShipment: (id, accessToken, options = {}) =>
        apiFetch(
          `/admin/build-orders/${encodeURIComponent(id)}/shipments/retry`,
          {
            ...options,
            method: "POST",
            accessToken,
          },
        ),
      markDroppedOff: (id, accessToken, options = {}) =>
        apiFetch(
          `/admin/build-orders/${encodeURIComponent(id)}/shipments/dropped-off`,
          {
            ...options,
            method: "POST",
            accessToken,
          },
        ),
      voidShipment: (id, accessToken, options = {}) =>
        apiFetch(
          `/admin/build-orders/${encodeURIComponent(id)}/shipments/void`,
          {
            ...options,
            method: "POST",
            accessToken,
          },
        ),
      downloadLabel: async (id, labelIndex, accessToken, options = {}) =>
        saveBlob(
          await apiDownload(
            `/admin/build-orders/${encodeURIComponent(id)}/labels/${labelIndex}`,
            {
              ...options,
              accessToken,
            },
          ),
        ),
      remove: (id, accessToken, options = {}) =>
        apiFetch(`/admin/build-orders/${encodeURIComponent(id)}`, {
          ...options,
          method: "DELETE",
          accessToken,
        }),
    },
    reservations: {
      list: (filters, accessToken, options = {}) =>
        apiFetch(`/admin/reservations${getQueryString(filters)}`, {
          ...options,
          accessToken,
        }),
      update: (id, updates, accessToken, options = {}) =>
        apiFetch(`/admin/reservations/${encodeURIComponent(id)}`, {
          ...options,
          method: "PATCH",
          body: updates,
          accessToken,
        }),
      remove: (id, accessToken, options = {}) =>
        apiFetch(`/admin/reservations/${encodeURIComponent(id)}`, {
          ...options,
          method: "DELETE",
          accessToken,
        }),
    },
  },
};
